// ---------------------------------------------------------------------------
// Universal Link / Deep Link handler — iOS Capacitor only
//
// When the app is installed on iOS and a user taps a youareyou.io link (e.g.
// via SMS), iOS intercepts the navigation and opens the app instead of
// Safari. Capacitor fires an `appUrlOpen` event with the original URL.
//
// We extract the path (and query/hash) and push it to React Router so the
// correct page is rendered.
//
// Routes handled:
//   /v/:code                    → ConnectionRequestPage (global trust link)
//   /c/:uuid                    → ConnectionRequestPage (direct link)
//   /start-verification/:uuid   → Start-verification interstitial
//   any other path              → passed through as-is
//
// Runs only on native iOS — no-ops on web (isNative === false).
// ---------------------------------------------------------------------------

import { App as CapApp } from '@capacitor/app'
import { isNative } from '@/lib/platform'

type NavigateFn = (path: string) => void

let registered = false

/**
 * Register the Universal Link listener once, early in the app lifecycle.
 * Pass the React Router `navigate` function so we can route without a full
 * page reload.
 *
 * Call this once from AuthContext (or App.tsx) after the router is mounted.
 */
export function registerDeepLinkHandler(navigate: NavigateFn): void {
  if (!isNative || registered) return
  registered = true

  CapApp.addListener('appUrlOpen', ({ url }) => {
    try {
      const parsed = new URL(url)
      const path = parsed.pathname + parsed.search + parsed.hash
      navigate(path)
    } catch {
      // Malformed URL — ignore
    }
  })
}
